/**
 * The browser's cap on peer connections.
 *
 * Chrome refuses a new RTCPeerConnection past a per-process limit ("Cannot
 * create so many PeerConnections"); closed ones count until they are garbage
 * collected. At the cap every attempt failed, and each failure retried five
 * times per peer, so the page stayed at the cap and logged a refusal a second
 * (the observatory CameraMap, 2026-10-21). After a refusal, peer creation
 * pauses for the whole page — the cap is the process's, not one group's — for
 * 1 min, then 2, 5 and 10 min while refusals continue; peers use the WebSocket
 * relay meanwhile. A connection created after a pause ends the escalation.
 */

export const PEER_LIMIT_PAUSE_MS = [60_000, 120_000, 300_000, 600_000]
export const PEER_LIMIT_CODE = 'PEER_LIMIT'

const state = { until: 0, strikes: 0 }
let now = () => Date.now()

/** Whether a failure is the browser's refusal. */
export function isPeerLimitError(err) {
    return /Cannot create so many PeerConnections/i.test(err?.message || String(err ?? ''))
}

/** Whether peer creation is paused now. */
export function peerLimitActive() {
    return now() < state.until
}

/** Ms left in the pause (0: not paused). */
export function peerLimitRemainingMs() {
    return Math.max(0, state.until - now())
}

/**
 * Record a refusal. @returns the new pause in ms, or 0 when already paused
 * (a refusal during a pause is one already counted: no escalation, no log)
 */
export function notePeerLimit() {
    if (peerLimitActive()) return 0
    state.strikes++
    const pause = PEER_LIMIT_PAUSE_MS[Math.min(state.strikes, PEER_LIMIT_PAUSE_MS.length) - 1]
    state.until = now() + pause
    console.warn(`[webrtc] the browser refuses more peer connections (Chrome's per-page cap): no new ones for ${Math.round(pause / 1000)} s, using the WebSocket relay meanwhile`)
    return pause
}

/** A peer connection was created: a pause that ended is behind us. */
export function notePeerCreated() {
    if (state.strikes && !peerLimitActive()) state.strikes = 0
}

/** The error createPeer throws while paused. */
export function peerLimitError(peerId) {
    const err = new Error(`peer connections paused for ${Math.ceil(peerLimitRemainingMs() / 1000)} s (browser cap): ${peerId}`)
    err.code = PEER_LIMIT_CODE
    return err
}

/** Tests only. */
export function _resetPeerLimit(clock) {
    state.until = 0
    state.strikes = 0
    now = clock || (() => Date.now())
}
